/* eslint-disable react/prop-types */

import InputWithIcon from '@deliveroo/tools-component-library/dist/components/InputWithIcon';
import InputLabel from '@deliveroo/tools-component-library/dist/components/InputLabel';
import Text from '@deliveroo/tools-component-library/dist/components/Text';
import LayoutBox from '@deliveroo/tools-component-library/dist/components/LayoutBox';
import Button from '@deliveroo/tools-component-library/dist/components/Button';
import Heading from '@deliveroo/tools-component-library/dist/components/Heading';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import Debug from 'debug';

import withI18n from 'lib/hocs/withI18n';
import withTracking from 'lib/hocs/withTracking';

import styles from './styles.module.scss';

const debug = Debug('rs:pages/login');

/**
 * A dumb props-driven presentational component,
 * unaware of Next or Redux.
 */
class LoginPage extends Component {
  static propTypes = {
    i18n: withI18n.TYPE.isRequired,
    tracker: withTracking.TYPE.isRequired,
    onLoginSuccess: PropTypes.func.isRequired,
    error: PropTypes.shape({
      status: PropTypes.number,
    }),
  };

  static defaultProps = {
    error: undefined,
  };

  state = {
    isLoading: false,
  };

  onSubmit = (e) => {
    const {
      actions: { login },
      tracker,
    } = this.props;

    e.preventDefault();
    this.setState({ isLoading: true });
    tracker.track('clicked_element', {
      element_type: 'button',
      label: 'Log in',
    });

    const [emailInput, passwordInput] = e.target.getElementsByTagName('input');
    const email = emailInput.value;
    const password = passwordInput.value;

    return login(email, password)
      .then(this.props.onLoginSuccess)
      .catch((err) => {
        debug('error during login', err);
        this.setState({ isLoading: false });
      });
  };

  // TODO consider using lib/utils/helpCentreArticleUrlForLanguage
  getCountryLearnMoreLink(countryCode) {
    const links = {
      IT: 'https://help.deliveroo.com/it/articles/2241148-gestisci-il-tuo-ristorante-ovunque-ti-trovi-grazie-al-restaurant-hub',
      GB: 'https://help.deliveroo.com/en/articles/2241148-manage-your-restaurant-from-anywhere-with-the-restaurant-hub',
      ES: 'https://help.deliveroo.com/es/articles/2241148-controla-tu-restaurante-desde-donde-quieras-con-restaurant-hub',
      FR: 'https://help.deliveroo.com/fr/articles/2241148-gerez-votre-restaurant-ou-que-vous-soyez-grace-au-restaurant-hub',
      NL: 'https://help.deliveroo.com/nl/articles/2241148-beheer-je-account-waar-je-maar-wilt-met-restaurant-hub',
      HK: 'https://help.deliveroo.com/zh-TW/articles/2241148-%E4%BD%BF%E7%94%A8-restaurant-hub-%E9%9A%A8%E6%99%82%E9%9A%A8%E5%9C%B0%E7%AE%A1%E7%90%86%E9%A4%90%E5%BB%B3',
    };

    return links[countryCode] || links.GB;
  }

  renderErrorMessage(status) {
    const { i18n } = this.props;

    switch (status) {
      case undefined:
        return undefined;
      case 401:
        return (
          <Text color="red">{i18n.gettext('login.form.incorrect_login')}</Text>
        );
      default:
        return (
          <Text color="red">{i18n.gettext('login.form.generic_error')}</Text>
        );
    }
  }

  render() {
    const { i18n, countryCode } = this.props;
    const { isLoading } = this.state;
    const { error } = this.props;
    const errorStatusCode = error && error.status;
    const learnMoreLink = this.getCountryLearnMoreLink(countryCode);

    return (
      <div className={styles.page}>
        <div className={styles.right}>
          <LayoutBox className={styles.login}>
            <form className={styles.form} onSubmit={this.onSubmit}>
              <div className={styles.narrower}>
                <LayoutBox spacing="0 0 large 0">
                  <img
                    height="31"
                    src="/static/images/logos/teal.svg"
                    alt={i18n.gettext('login.form.logo_alt')}
                  />
                </LayoutBox>
                <Heading level={1} size="large" spacing="medium 0 medium 0">
                  {i18n.gettext('login.heading')}
                </Heading>
              </div>
              <Text spacing="0 0 medium 0">{i18n.gettext('login.prompt')}</Text>
              <div className={styles.narrower}>
                <InputLabel>
                  {i18n.gettext('login.form.email_label')}
                  <InputWithIcon
                    spacing="0 0 medium 0"
                    placeholder={i18n.gettext(
                      'login.form.email_input_placeholder',
                    )}
                    icon="email"
                    type="email"
                  />
                </InputLabel>
                <InputLabel>
                  {i18n.gettext('login.form.password_label')}
                  <InputWithIcon
                    spacing="0 0 medium 0"
                    placeholder={i18n.gettext(
                      'login.form.password_input_placeholder',
                    )}
                    icon="lock_closed"
                    type="password"
                  />
                </InputLabel>
                {errorStatusCode && (
                  <LayoutBox spacing="medium 0">
                    {this.renderErrorMessage(errorStatusCode)}
                  </LayoutBox>
                )}
                <Button
                  label={i18n.gettext('login.form.submit_default_label')}
                  layout="block"
                  isLoading={isLoading}
                  spacing="0 0 medium 0"
                />
                <LayoutBox spacing="medium 0 medium 0">
                  <Link href="/forgotten-password">
                    <a>
                      <Text className={styles.forgottenPassword} color="teal">
                        {i18n.gettext('login.form.forgotten_password_link')}
                      </Text>
                    </a>
                  </Link>
                </LayoutBox>
              </div>
            </form>
          </LayoutBox>
        </div>
        <div className={styles.hero}>
          <div className={styles.contactWrapper}>
            <LayoutBox spacing="large 0 medium 0" className={styles.contact}>
              <a href={learnMoreLink} target="_blank" rel="noopener noreferrer">
                <Heading
                  level={3}
                  size="x-small"
                  spacing="0 0 small 0"
                  color="white"
                >
                  {i18n.gettext('login.learn_more')}
                </Heading>
              </a>
            </LayoutBox>
          </div>
          <div className={styles.content}>
            <div className={styles.slant} />
            <img alt="" src="/static/images/chart_illustration.svg" />
          </div>
        </div>
      </div>
    );
  }
}

export default withI18n(LoginPage);
